import { Box, Card, CardHeader, CardContent, useTheme, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { merge } from "lodash";
import { useGetList, useGetOne } from "ra-core";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LineChartResponseDto, LineChartWithPairsResponseDto, TimeRangeType } from "@/gen/api/metrics";
import { generalizeData, LineChartDataEx, genericChartOptions, perfThreshold, ChartLegends } from "../databases/metricsChartCommon";
import Chart from 'react-apexcharts'
import { useSelector } from "react-redux";
import { RecPatch } from "@/dataProvider/tools";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { tbgSxProps } from "../overrides/toggleButtonGroupSx";



interface TimeoutNumChartProps {
  timeRange:TimeRangeType, 
  endTime:string, 
  setTimeRange: (tr:TimeRangeType) => void
}


const TimeoutNumChart: FC<TimeoutNumChartProps> = ({
  timeRange,
  setTimeRange,
  endTime,
}) => {
  const theme = useTheme();

  const { data, loading: loading1 } = useGetList<LineChartWithPairsResponseDto & RecPatch>(
    'dbmetrics-timeoutnum', pages(), sorts(), {timeRange, endTime})
  const timeoutNumData: LineChartWithPairsResponseDto & RecPatch | undefined = (data || {})['-1'] // [timeRangeType + '-' + endTime]

  const finalData = useMemo(() => generalizeData(theme, timeoutNumData, [
    theme.palette.warning.dark,
    theme.palette.success.light,
    theme.palette.primary.light,
  ]), [theme, timeoutNumData])

  const [selectedSeries, setSelectedSeries] = useState(finalData.legend);
  const selectedSeriesRef = useRef(selectedSeries)
  useEffect(() => {
    setSelectedSeries(finalData.legend)
  }, [finalData])
  
  useEffect(() => {
    selectedSeriesRef.current = selectedSeries
  }, [selectedSeries])
  const handleChange = useCallback((x: LineChartDataEx, checked: boolean) => {
    if (!checked) {
      setSelectedSeries(selectedSeriesRef.current.filter((item) => item !== x.name));
    } else {
      setSelectedSeries([...selectedSeriesRef.current, x.name]);
    }
  }, []);

  const visibleSeries = useMemo(() => finalData.series.filter((item) => item.name && selectedSeries.includes(item.name)), [finalData, selectedSeries]);

  const genericOptions = useMemo(() => genericChartOptions(theme), [theme])

  const chart = useMemo(() => {
    const datapointCount = visibleSeries.map(s => s.data.length).reduce((acc, n) => acc + n, 0)

    const erase = {
      options: {
        ...(datapointCount > perfThreshold ? { markers: null } : {}),
      },
    }
    const patch = {
      options: {
        markers: { size: 0 },
      },
    }
    return merge({}, genericOptions, {
      options: {
        colors: visibleSeries.map((item) => item.color),
        tooltip: {
          x: { format: 'dd MMM / HH:mm' }
        },
        xaxis: {
          type: 'datetime',
          categories: finalData.xaxis,
        },
        yaxis: [{
          labels: {
            formatter: (value:any) => value,
          },
        }],
      },
      series: visibleSeries.map(s => ({
        ...s,
        data: s.data.map(d => Number(d))
      })),
    }, erase, patch)
  }, [genericOptions, visibleSeries, finalData]);  

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
      }}
    >
      <Card>
        <CardHeader 
          title="Number of Timeouts" 
          action={
            <ToggleButtonGroup
              size="small"
              sx={{
                ...tbgSxProps,
                mr: 2,
              }}
              exclusive
              value={timeRange}
              onChange={(e:any, v:TimeRangeType) => setTimeRange(v)}
            >
              <ToggleButton value={TimeRangeType.Hour}>
                <Typography variant="subtitle2" color="textSecondary">H</Typography>
              </ToggleButton>
              <ToggleButton value={TimeRangeType.Day}>
                <Typography variant="subtitle2" color="textSecondary">D</Typography>
              </ToggleButton>
              <ToggleButton value={TimeRangeType.Week}>
                <Typography variant="subtitle2" color="textSecondary">W</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <CardContent>
          <ChartLegends series={finalData.series} onClick={handleChange} />
          <Chart
            height="350"
            type="line"
            {...chart}
          />
        </CardContent>
      </Card>
    </Box>
  )
}

export default TimeoutNumChart;
